<script setup>
/* eslint-disable vue/custom-event-name-casing */
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import conversationAPI from '../../../../api/inbox/conversation';
import PipelineAPI from '../../../../api/pipelineCards';
import dealStatusAPI from '../../../../api/pipeline/dealStatus';
import AttributeAPI from '../../../../api/attributes';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { useRouter } from 'vue-router';
import Modal from 'dashboard/components/Modal.vue';
import SendMessageTemplate from './SendMessageTemplate.vue';
import PipelineCardHero from './card-detail/PipelineCardHero.vue';
import PipelineStageStepper from 'dashboard/components-next/pipeline/PipelineStageStepper.vue';
import CardInfoPanel from './card-detail/CardInfoPanel.vue';
import QualificationPanel from './card-detail/QualificationPanel.vue';
import GoogleCalendarSync from './GoogleCalendarSync.vue';
import CardAssigneePanel from './card-detail/CardAssigneePanel.vue';
import MarkAsWonModal from './MarkAsWonModal.vue';
import MarkAsLostModal from './MarkAsLostModal.vue';
import AttachmentsTab from './card-detail/tabs/AttachmentsTab.vue';
import ChecklistTab from './card-detail/tabs/ChecklistTab.vue';
import NotesTab from './card-detail/tabs/NotesTab.vue';
import ScheduledMessagesTab from './card-detail/tabs/ScheduledMessagesTab.vue';
import CrmActivitiesTab from './card-detail/tabs/CrmActivitiesTab.vue';
import RelatedItemsTab from './card-detail/tabs/RelatedItemsTab.vue';
import OpportunitiesTab from './card-detail/tabs/OpportunitiesTab.vue';
import LeadScoreOverrideModal from './LeadScoreOverrideModal.vue';
import contactAPI from '../../../../api/contacts';
import {
  findPipelineForCard,
  orderedStagesForPipeline,
} from '../helpers/pipelineStageResolver';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import { countStageChanges } from '../helpers/stageChangeCount';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

// Definição dos props e emits
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'item-updated', 'deleted']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { isCloudFeatureEnabled } = useAccount();

// Fase 59 — Oportunidades é cross feature opt-in por conta: com ela desligada o
// card continua fechando (Ganho/Perdido com valor), mas nada do razão de vendas
// aparece — nem a aba, nem o formulário de produto no modal de ganho.
const isOpportunitiesEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.PIPELINE_OPPORTUNITIES)
);

const activeTab = ref('notes');

// Refs para modais e menus
const showQuickActionsMenu = ref(false);
const showMessageModal = ref(false);

// Adicione estas refs
const customAttributes = ref([]);
const contactCustomAttributes = ref([]);

// Checklist state (kept in parent for Statistics card)
const checklistItems = ref(props.item.item_details?.checklist || []);

// Critérios de qualificação vêm do funil do card (auditoria G3).
const savingQualification = ref(false);
const qualificationCriteria = computed(() => {
  const criteria =
    props.item?.pipeline?.settings?.lead_scoring?.qualification_criteria;
  return Array.isArray(criteria) ? criteria : [];
});

// Cross-review Codex: `emit('item-updated')` não é aguardável, então liberar os
// controles logo depois deixaria o painel mostrando o estado ANTIGO por um
// instante — e aceitando um segundo clique em cima dele. O checklist recém-
// salvo fica guardado aqui e tem precedência até o card novo chegar.
// Cross-review Codex: o pendente carrega o ID do card. Sem isso, trocar de card
// (ou um item chegando fora de ordem) aplicaria no card errado o checklist que
// acabou de ser salvo em outro.
const pendingQualification = ref(null);

const qualificationChecklist = computed(() => {
  const pending = pendingQualification.value;
  if (pending && pending.cardId === props.item?.id) return pending.checklist;

  return props.item?.qualification_checklist || {};
});

// Cross-review Codex: limpar o pendente a cada mudança de checklist o
// descartaria mesmo quando o item que chegou ainda é o ANTIGO. Ele só sai
// quando o card muda, ou quando o servidor confirma o mesmo conteúdo.
const checklistFingerprint = checklist =>
  JSON.stringify(
    Object.entries(checklist || {})
      .map(([id, value]) => [id, Boolean(value?.checked)])
      .sort(([a], [b]) => a.localeCompare(b))
  );

watch(
  () => [props.item?.id, props.item?.qualification_checklist],
  ([cardId, incoming]) => {
    const pending = pendingQualification.value;
    if (!pending) return;

    if (pending.cardId !== cardId) {
      pendingQualification.value = null;
      return;
    }

    if (
      checklistFingerprint(incoming) === checklistFingerprint(pending.checklist)
    ) {
      pendingQualification.value = null;
    }
  }
);

const handleQualificationUpdate = async checklist => {
  // O id é capturado ANTES do await: trocar de card com a requisição em voo
  // não pode fazer a resposta de A ser atribuída a B (cross-review Codex).
  const cardId = props.item.id;
  savingQualification.value = true;
  try {
    const updated = await store.dispatch(
      'pipelineCards/updateQualificationChecklist',
      { itemId: cardId, checklist }
    );
    pendingQualification.value = {
      cardId,
      checklist: updated?.qualification_checklist || checklist,
    };
    emit('item-updated');
  } finally {
    savingQualification.value = false;
  }
};

// Contact avatar URL for header (same logic as PipelineCard.vue)
const contactAvatarUrl = computed(() => {
  const itemDetails = props.item?.item_details;
  if (itemDetails?.contact_thumbnail && itemDetails.contact_thumbnail.trim()) {
    return itemDetails.contact_thumbnail;
  }
  const conversationContact = itemDetails?.conversation?.contact;
  if (conversationContact?.thumbnail && conversationContact.thumbnail.trim()) {
    return conversationContact.thumbnail;
  }
  if (
    conversationContact?.avatar_url &&
    conversationContact.avatar_url.trim()
  ) {
    return conversationContact.avatar_url;
  }
  if (props.item?.contact?.thumbnail && props.item.contact.thumbnail.trim()) {
    return props.item.contact.thumbnail;
  }
  return '';
});

// Contact name for header subtitle (only shown when different from title)
const contactName = computed(() => {
  return props.item?.contact?.name || props.item?.item_details?.company || '';
});

const cardTitle = computed(() => {
  return (
    props.item?.title ||
    props.item?.item_details?.title ||
    t('PIPELINE.UNTITLED_ITEM')
  );
});

const showSubtitle = computed(() => {
  return contactName.value && contactName.value !== cardTitle.value;
});

// Ref para armazenar os dados da conversa
const conversationData = ref(props.item.item_details?.conversation || null);
const conversationDataLoading = ref(false);
const conversationDataError = ref(false);
let conversationRequestId = 0;
let conversationController = null;

const normalizePositiveId = value => {
  const normalizedValue = String(value ?? '').trim();
  return /^[1-9]\d*$/.test(normalizedValue) ? normalizedValue : '';
};
const currentItemContactId = () =>
  normalizePositiveId(
    props.item?.contact_id ||
      props.item?.contact?.id ||
      props.item?.item_details?.conversation?.contact?.id
  );

// Whether a conversation is linked (for direct link button)
const hasLinkedConversation = computed(() => {
  return !!(
    conversationData.value || props.item?.item_details?.conversation_id
  );
});

const pipelines = computed(() => store.getters['pipelines/getPipelines'] || []);
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const itemPipeline = computed(() =>
  findPipelineForCard(
    pipelines.value,
    selectedPipeline.value,
    props.item?.pipeline_id
  )
);
const { canDeleteCard } = usePipelinePermissions(itemPipeline);

// Resolve a stage ID using the pipeline that owns this card.
const resolveStageName = stageId => {
  if (!stageId) return '-';
  return itemPipeline.value?.stages?.[stageId]?.name || stageId;
};

// Computed para obter o nome do estágio atual
const currentStageName = computed(() => {
  if (!props.item.pipeline_stage) return '';
  return resolveStageName(props.item.pipeline_stage);
});

// ── Stepper: ordered stages from the selected pipeline (dynamic, never hardcoded) ─
const orderedStages = computed(() => {
  return orderedStagesForPipeline(itemPipeline.value);
});

// ── Assignee panel data ────────────────────────────────────────────────────────
const accountAgents = computed(() => store.getters['agents/getAgents'] || []);

const assignedAgentIds = computed(() => {
  const owner = props.item?.owner;
  return owner ? [owner.id] : [];
});

// ── Stage changes count (for CardInfoPanel statistics) ────────────────────────
// Lógica pura extraída para helpers/stageChangeCount.js — ver lá o porquê de
// não mesclar as três fontes de histórico (F24).
const stageChangesCount = computed(() => countStageChanges(props.item));

// ── Move loading state ────────────────────────────────────────────────────────
const isLoadingMove = ref(false);

// ── Won / Lost modal visibility (declared early — used in handleRequestTerminalMove below) ──
const showWonModal = ref(false);
const showLostModal = ref(false);
const showLeadScoreModal = ref(false);

// ── Stepper handlers ──────────────────────────────────────────────────────────
const handleMoveToStage = async newStageId => {
  try {
    isLoadingMove.value = true;
    await PipelineAPI.moveToStage(
      props.item.id,
      newStageId,
      props.item.pipeline_stage
    );
    emit('item-updated');
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.STAGE_UPDATED_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    isLoadingMove.value = false;
  }
};

const handleRequestTerminalMove = stageId => {
  const stage = orderedStages.value.find(s => s.id === stageId);
  if (!stage) return;
  if (stage.terminalType === 'won') {
    showWonModal.value = true;
  } else if (stage.terminalType === 'lost') {
    showLostModal.value = true;
  } else {
    handleMoveToStage(stageId);
  }
};

// ── Assignee handler ──────────────────────────────────────────────────────────
const handleAssignAgents = async newAgentIds => {
  try {
    const accountId = store.getters.getCurrentAccountId;
    const ownerId = newAgentIds[0] || null;
    // Auditoria 2026-08-08 (F01): a chave é `owner_id`. Enquanto isto mandava
    // `user_id`, o backend não achava o dono, salvava `owner: nil` e respondia
    // sucesso — escolher um agente desatribuía o card e o antigo voltava no
    // próximo save (sync_agent_and_owner). Reproduzido em chatdev no card 19063.
    await PipelineAPI.assignOwner(accountId, props.item.id, {
      owner_id: ownerId,
    });
    emit('item-updated');
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_SAVE_ERROR'),
      action: { type: 'error' },
    });
  }
};

// ── Panel field update handler ────────────────────────────────────────────────
const handlePanelFieldUpdate = async (fieldName, newValue) => {
  const updateData = {};
  if (fieldName.startsWith('custom_attributes.')) {
    const key = fieldName.replace('custom_attributes.', '');
    const current = { ...(props.item.custom_attributes || {}) };
    current[key] = newValue;
    updateData.custom_attributes = current;
  } else {
    updateData[fieldName] = newValue;
  }
  try {
    await PipelineAPI.updateCard(props.item.id, updateData);
    emit('item-updated');
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_UPDATED_SUCCESS'),
      action: { type: 'success' },
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_SAVE_ERROR'),
      action: { type: 'error' },
    });
  }
};

const isAdmin = canDeleteCard;

// Delete confirmation modal
const showDeleteConfirmModal = ref(false);
const isDeleting = ref(false);
const deleteConfirmationContext = ref(null);
let deleteController = null;

// Adicione esta função para buscar os atributos disponíveis
const fetchCustomAttributes = async () => {
  try {
    const response = await AttributeAPI.getAttributesByModel();
    customAttributes.value = response.data.filter(
      attr => attr.attribute_model === 'pipeline_card_attribute'
    );
    contactCustomAttributes.value = response.data.filter(
      attr => attr.attribute_model === 'contact_attribute'
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[PipelineCardDetails] fetchCustomAttributes failed:', error);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.LOAD_ATTRIBUTES_ERROR'),
      action: { type: 'error' },
    });
  }
};

const linkedConversationDisplayId = () =>
  props.item.conversation_display_id ||
  props.item.item_details?.conversation?.display_id;

const currentConversationContext = () => ({
  accountId: normalizePositiveId(store.getters.getCurrentAccountId),
  cardId: normalizePositiveId(props.item?.id),
  contactId: currentItemContactId(),
  conversationDisplayId: normalizePositiveId(linkedConversationDisplayId()),
  conversationId: normalizePositiveId(
    props.item?.item_details?.conversation_id ||
      props.item?.item_details?.conversation?.id
  ),
});
const conversationContextMatches = context => {
  if (!context) return false;
  const currentContext = currentConversationContext();
  return Object.keys(currentContext).every(
    key => currentContext[key] === context[key]
  );
};
const cancelConversationRequest = () => {
  conversationRequestId += 1;
  conversationController?.abort();
  conversationController = null;
  conversationDataLoading.value = false;
};

// The conversation endpoint receives and returns the display id (`data.id`).
// The card also keeps an internal id in item_details, but that identifier is not
// exposed by this endpoint. Validate the response's account, display id and
// contact before rendering it.
const fetchConversationData = async () => {
  const context = currentConversationContext();
  cancelConversationRequest();
  const requestId = conversationRequestId;
  if (
    !context.accountId ||
    !context.cardId ||
    !context.conversationDisplayId ||
    !context.conversationId
  ) {
    conversationDataError.value = false;
    return;
  }

  const controller = new AbortController();
  conversationController = controller;
  conversationDataLoading.value = true;
  conversationDataError.value = false;
  try {
    const response = await conversationAPI.show(context.conversationDisplayId, {
      accountId: context.accountId,
      signal: controller.signal,
    });
    if (
      controller.signal.aborted ||
      requestId !== conversationRequestId ||
      !conversationContextMatches(context)
    ) {
      return;
    }

    const responseConversationDisplayId = normalizePositiveId(
      response.data?.display_id || response.data?.id
    );
    const responseAccountId = normalizePositiveId(response.data?.account_id);
    const responseContactId = normalizePositiveId(
      response.data?.contact?.id || response.data?.meta?.sender?.id
    );
    if (
      responseConversationDisplayId !== context.conversationDisplayId ||
      responseAccountId !== context.accountId ||
      responseContactId !== context.contactId
    ) {
      conversationData.value = null;
      conversationDataError.value = true;
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.MESSAGES.CONVERSATION_LOAD_ERROR'),
        action: { type: 'error' },
      });
      return;
    }
    conversationData.value = response.data || null;
  } catch {
    if (
      controller.signal.aborted ||
      requestId !== conversationRequestId ||
      !conversationContextMatches(context)
    ) {
      return;
    }
    conversationDataError.value = true;
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.CONVERSATION_LOAD_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    if (
      requestId === conversationRequestId &&
      conversationContextMatches(context)
    ) {
      conversationDataLoading.value = false;
    }
    if (conversationController === controller) conversationController = null;
  }
};

const retryConversationData = () => fetchConversationData();

watch(
  () => [
    ...Object.values(currentConversationContext()),
    props.item?.item_details?.conversation,
  ],
  () => {
    cancelConversationRequest();
    const embeddedConversation = props.item?.item_details?.conversation || null;
    conversationData.value = embeddedConversation;
    conversationDataError.value = false;
    if (
      !embeddedConversation &&
      currentConversationContext().conversationDisplayId &&
      currentConversationContext().conversationId
    ) {
      fetchConversationData();
    }
  },
  { immediate: true, flush: 'sync' }
);

// Notes state (kept in parent for Statistics card and passed to NotesTab/AttachmentsTab)
const notes = ref([]);

// Watch para manter notes sincronizado com as mudanças do item
watch(
  () => props.item.item_details?.notes,
  newNotes => {
    if (newNotes) {
      notes.value = [...newNotes].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
    }
  },
  { immediate: true }
);

// ── Attachments count (mirrors AttachmentsTab.vue logic) ─────────────────────
// Declared after `notes` ref to satisfy no-use-before-define.
const attachmentsCount = computed(() => {
  const noteAttachments = notes.value.reduce((acc, note) => {
    return (
      acc + (Array.isArray(note.attachments) ? note.attachments.length : 0)
    );
  }, 0);
  const itemDetailsCount = props.item?.item_details?.attachments?.length || 0;
  const activeStorageCount = props.item?.attachments?.length || 0;

  // Deduplicate by id (same logic as getAllAttachments in AttachmentsTab)
  const allRaw = [
    ...notes.value.flatMap(n =>
      Array.isArray(n.attachments) ? n.attachments : []
    ),
    ...(props.item?.item_details?.attachments || []),
    ...(props.item?.attachments || []),
  ];
  const seen = new Set();
  const unique = allRaw.filter(att => {
    const key = att?.id?.toString();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return (
    unique.length || noteAttachments + itemDetailsCount + activeStorageCount
  );
});

const showMoveModal = ref(false);

// ── Keyboard shortcuts ────────────────────────────────────────────────────────
// Mobile drawer toggles (hidden on lg+ screens)
const showLeftDrawer = ref(false);
const showRightDrawer = ref(false);
// Related items drawer (triggered from header button)
const showRelatedDrawer = ref(false);

const handleKeydown = event => {
  // Ignore when user is typing in a form field
  const tag = event.target?.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || event.target?.isContentEditable)
    return;
  // Ignore with modifiers
  if (event.ctrlKey || event.metaKey || event.altKey) return;

  switch (event.key) {
    case 'e':
      event.preventDefault();
      window.dispatchEvent(new CustomEvent('pipeline-card:focus-title'));
      break;
    case 's':
      event.preventDefault();
      window.dispatchEvent(new CustomEvent('pipeline-card:focus-status'));
      break;
    case 'n':
      event.preventDefault();
      activeTab.value = 'notes';
      nextTick(() => {
        const textarea = document.querySelector('[data-pipeline-notes-input]');
        textarea?.focus();
      });
      break;
    default:
      break;
  }
};

onMounted(() => {
  fetchCustomAttributes();

  document.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
  cancelConversationRequest();
  document.removeEventListener('keydown', handleKeydown);
});

// Adicione uma função para navegar para uma conversa
const navigateToConversation = async () => {
  // Usar display_id da conversa vinculada ao card
  const displayId =
    conversationData.value?.display_id || props.item.conversation_display_id;

  if (!displayId) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.CONVERSATION_NAVIGATION_ERROR'),
      action: { type: 'error' },
    });
    return;
  }

  // Obter o account_id de forma mais segura
  const accountId = store.getters.getCurrentAccountId;

  // Fechar o menu de ações rápidas
  showQuickActionsMenu.value = false;

  try {
    await router.push({
      name: 'inbox_conversation',
      params: {
        accountId,
        conversation_id: displayId,
      },
    });
  } catch (error) {
    // Fallback: navegação direta pela URL com account_id atual
    window.location.href = `/app/accounts/${accountId}/conversations/${displayId}`;
  }
};

// Adicione ao script
const tabs = computed(() => [
  { id: 'notes', icon: 'document', label: t('PIPELINE.TABS.NOTES') },
  {
    id: 'checklist',
    icon: 'checkmark-circle',
    label: t('PIPELINE.TABS.CHECKLIST'),
  },
  { id: 'attachments', icon: 'attach', label: t('PIPELINE.TABS.ATTACHMENTS') },
  {
    id: 'scheduled',
    icon: 'calendar-clock',
    label: t('PIPELINE.TABS.SCHEDULED_MESSAGES'),
  },
  // Fase 61 (G1) — as atividades/tarefas do CRM (`pipeline_activities`). NÃO é
  // o histórico do card: aquele vive em `item_details.activities`.
  {
    id: 'crm-activities',
    icon: 'clipboard',
    label: t('PIPELINE.CRM_ACTIVITIES.TAB'),
  },
  // Fase 59 — histórico financeiro do card (quanto este cliente já comprou).
  // Cross feature: a conta sem `pipeline_opportunities` fecha negócio do mesmo
  // jeito, só não tem razão de vendas — e a aba não existe para ela.
  ...(isOpportunitiesEnabled.value
    ? [
        {
          id: 'opportunities',
          // `money` não existe no catálogo (dashboard-icons.json não tem nenhum
          // ícone financeiro) e imprimia "Icon not found: money-outline" no
          // console a cada abertura de card. Tendência é o que o razão mostra:
          // quanto este cliente já comprou ao longo do tempo.
          icon: 'arrow-trending-lines',
          label: t('PIPELINE.OPPORTUNITIES.TAB'),
        },
      ]
    : []),
]);

const handleTabKeydown = (event, index) => {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  event.preventDefault();
  let nextIndex = index;
  if (event.key === 'ArrowLeft') {
    nextIndex = (index - 1 + tabs.value.length) % tabs.value.length;
  } else if (event.key === 'ArrowRight') {
    nextIndex = (index + 1) % tabs.value.length;
  } else if (event.key === 'Home') {
    nextIndex = 0;
  } else if (event.key === 'End') {
    nextIndex = tabs.value.length - 1;
  }
  activeTab.value = tabs.value[nextIndex].id;
  nextTick(() => {
    document.getElementById(`pipeline-card-tab-${activeTab.value}`)?.focus();
  });
};

// ── Inline title update ───────────────────────────────────────────────────────
const handleTitleUpdate = async newTitle => {
  try {
    await PipelineAPI.updateCard(props.item.id, { title: newTitle });
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_UPDATED_SUCCESS'),
      action: { type: 'success' },
    });
    emit('item-updated');
  } catch (e) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_SAVE_ERROR'),
      action: { type: 'error' },
    });
  }
};

// ── Inline priority update ────────────────────────────────────────────────────
// Send ONLY the delta — backend does deep_merge from the DB row, so spreading
// `...props.item.item_details` (which can be stale) would resurrect old
// values for keys like `checklist` that were just modified by another tab.
const handlePriorityUpdate = async newPriority => {
  try {
    await PipelineAPI.updateCard(props.item.id, {
      priority: newPriority,
      item_details: { priority: newPriority },
    });
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_UPDATED_SUCCESS'),
      action: { type: 'success' },
    });
    emit('item-updated');
  } catch (e) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_SAVE_ERROR'),
      action: { type: 'error' },
    });
  }
};

// ── Status change handlers (from Hero pills) ──────────────────────────────────
const handleRequestMarkWon = () => {
  showWonModal.value = true;
};

const handleRequestMarkLost = () => {
  showLostModal.value = true;
};

const handleRequestReopen = async () => {
  try {
    // The reopen endpoint returns the authoritative deal-status payload — pass
    // it up so the open card + board update instantly (no stale "Ganho/Perdido"
    // window while a follow-up GET is in flight).
    const { data } = await dealStatusAPI.reopen(props.item.id);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.CARD.STATUS.REOPENED_SUCCESS'),
      action: { type: 'success' },
    });
    emit('item-updated', data);
  } catch (e) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.CARD.STATUS.REOPENED_ERROR'),
      action: { type: 'error' },
    });
  }
};

const handleWonSuccess = payload => {
  showWonModal.value = false;
  emit('item-updated', payload);
};

const handleLostSuccess = payload => {
  showLostModal.value = false;
  emit('item-updated', payload);
};

// Função para duplicar o item
const handleDuplicateItem = async () => {
  try {
    const duplicatedItem = {
      pipeline_id: props.item.pipeline_id,
      pipeline_stage: props.item.pipeline_stage,
      contact_id: props.item.contact_id,
      owner_id: props.item.owner_id,
      // Auditoria 2026-08-08 (F05): `company_id`, `lead_score`, `probability` e
      // `qualification_status` não são aceitos por este endpoint — o último nem
      // existe no schema. Em troca, os campos abaixo eram aceitos e não vinham,
      // então a cópia saía sem descrição, receita e moeda.
      source: props.item.source,
      description: props.item.description,
      expected_revenue: props.item.expected_revenue,
      currency: props.item.currency,
      forecast_close_date: props.item.forecast_close_date,
      priority:
        props.item.priority || props.item.item_details?.priority || 'none',
      scheduled_at: props.item.scheduled_at,
      deadline: props.item.deadline,
      item_details: {
        ...props.item.item_details,
        title: `${props.item.item_details.title} (Cópia)`,
      },
    };

    await PipelineAPI.createCard(duplicatedItem);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_DUPLICATED_SUCCESS'),
      action: { type: 'success' },
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_DUPLICATED_ERROR'),
      action: { type: 'error' },
    });
  }
};

// Função para lidar com ações rápidas
const handleQuickAction = actionId => {
  switch (actionId) {
    case 'move':
      showMoveModal.value = true;
      break;
    case 'assign': {
      const accountId = store.getters.getCurrentAccountId;
      const contactId = props.item.item_details?.conversation?.contact?.id;

      if (!contactId) {
        return;
      }

      const routeConfig = {
        name: 'contacts_show',
        params: {
          accountId,
          id: contactId,
        },
      };

      try {
        router.push(routeConfig);
      } catch (err) {
        const fallbackUrl = `/app/accounts/${accountId}/contacts/${contactId}`;
        window.location.href = fallbackUrl;
      }
      break;
    }
    case 'chat':
    case 'send_message':
      showMessageModal.value = true;
      break;
    case 'duplicate':
      handleDuplicateItem();
      break;
    default:
      break;
  }
};

// Handle message sent
const handleMessageSent = () => {
  showMessageModal.value = false;
  // Optionally refresh item data
};

// ── Unlink conversation ───────────────────────────────────────────────────────
// CardInfoPanel já confirma a alteração pelo modal compartilhado antes de emitir.
const currentUnlinkContext = () => ({
  accountId: normalizePositiveId(store.getters.getCurrentAccountId),
  cardId: normalizePositiveId(props.item?.id),
  contactId: currentItemContactId(),
  conversationDisplayId: normalizePositiveId(
    props.item?.item_details?.conversation?.display_id ||
      props.item?.conversation_display_id
  ),
  conversationId: normalizePositiveId(
    props.item?.item_details?.conversation_id ||
      props.item?.item_details?.conversation?.id
  ),
});
const unlinkContextMatches = snapshot => {
  if (!snapshot) return false;
  const currentContext = currentUnlinkContext();
  return Object.keys(currentContext).every(
    key => currentContext[key] === snapshot[key]
  );
};

const handleUnlinkConversation = async unlinkSnapshot => {
  if (!unlinkContextMatches(unlinkSnapshot)) return;
  try {
    await PipelineAPI.updateCard(
      unlinkSnapshot.cardId,
      {
        conversation_display_id: null,
        item_details: {
          conversation_id: null,
          conversation: null,
        },
      },
      { accountId: unlinkSnapshot.accountId }
    );
    if (unlinkContextMatches(unlinkSnapshot)) {
      emit('item-updated');
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.MESSAGES.CONVERSATION_UNLINKED'),
        action: { type: 'success' },
      });
    }
  } catch {
    if (unlinkContextMatches(unlinkSnapshot)) {
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.MESSAGES.CONVERSATION_UNLINK_ERROR'),
        action: { type: 'error' },
      });
    }
  }
};

// ── Link conversation modal ───────────────────────────────────────────────────
const showLinkConversationModal = ref(false);
const linkConversationList = ref([]);
const linkConversationLoading = ref(false);
const linkConversationError = ref(false);
const linkConversationSaving = ref(false);
const linkConversationContactId = ref(null);
const selectedConversationId = ref(null);
const linkConversationContext = ref(null);
let linkConversationRequestId = 0;
let linkConversationController = null;
let linkConversationSaveController = null;

const linkContextMatches = context =>
  Boolean(
    context &&
      conversationContextMatches(context) &&
      linkConversationContext.value &&
      Object.keys(context).every(
        key => linkConversationContext.value[key] === context[key]
      )
  );
const linkConversationDisplayId = conversation =>
  normalizePositiveId(conversation?.display_id || conversation?.id);
const linkedCandidateContactId = conversation =>
  normalizePositiveId(
    conversation?.contact?.id || conversation?.meta?.sender?.id
  );
const linkedCandidateAccountId = conversation =>
  normalizePositiveId(conversation?.account_id);
const conversationBelongsToLinkContext = (conversation, context) =>
  linkConversationDisplayId(conversation) &&
  linkedCandidateAccountId(conversation) === context.accountId &&
  linkedCandidateContactId(conversation) === context.contactId;

const cancelLinkConversationRequests = ({ closeModal = true } = {}) => {
  linkConversationRequestId += 1;
  linkConversationController?.abort();
  linkConversationSaveController?.abort();
  linkConversationController = null;
  linkConversationSaveController = null;
  linkConversationLoading.value = false;
  linkConversationSaving.value = false;
  linkConversationError.value = false;
  linkConversationList.value = [];
  selectedConversationId.value = null;
  linkConversationContactId.value = null;
  linkConversationContext.value = null;
  if (closeModal) showLinkConversationModal.value = false;
};

const loadLinkConversations = async context => {
  if (!linkContextMatches(context)) return;
  linkConversationRequestId += 1;
  const requestId = linkConversationRequestId;
  linkConversationController?.abort();
  const controller = new AbortController();
  linkConversationController = controller;
  linkConversationLoading.value = true;
  linkConversationError.value = false;
  try {
    const response = await contactAPI.getConversations(context.contactId, {
      accountId: context.accountId,
      signal: controller.signal,
    });
    if (
      controller.signal.aborted ||
      requestId !== linkConversationRequestId ||
      !linkContextMatches(context)
    ) {
      return;
    }
    const conversations = Array.isArray(response.data?.payload)
      ? response.data.payload
      : [];
    linkConversationList.value = conversations.filter(conversation =>
      conversationBelongsToLinkContext(conversation, context)
    );
    if (conversations.length && !linkConversationList.value.length) {
      linkConversationError.value = true;
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.MESSAGES.CONVERSATIONS_LOAD_ERROR'),
        action: { type: 'error' },
      });
    }
  } catch {
    if (
      controller.signal.aborted ||
      requestId !== linkConversationRequestId ||
      !linkContextMatches(context)
    ) {
      return;
    }
    linkConversationError.value = true;
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.CONVERSATIONS_LOAD_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    if (
      requestId === linkConversationRequestId &&
      linkContextMatches(context)
    ) {
      linkConversationLoading.value = false;
    }
    if (linkConversationController === controller) {
      linkConversationController = null;
    }
  }
};

const handleLinkConversation = async () => {
  const context = currentConversationContext();

  if (!context.accountId || !context.cardId || !context.contactId) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.NO_CONTACT_TO_LINK'),
      action: { type: 'warning' },
    });
    return;
  }

  showLinkConversationModal.value = true;
  selectedConversationId.value = null;
  linkConversationContactId.value = context.contactId;
  linkConversationContext.value = context;
  await loadLinkConversations(context);
};

const retryLinkConversations = () => {
  if (linkContextMatches(linkConversationContext.value)) {
    loadLinkConversations(linkConversationContext.value);
  }
};

const confirmLinkConversation = async () => {
  const context = linkConversationContext.value
    ? { ...linkConversationContext.value }
    : null;
  const selectedId = normalizePositiveId(selectedConversationId.value);
  if (!selectedId || !linkContextMatches(context)) return;
  const conv = linkConversationList.value.find(
    candidate => linkConversationDisplayId(candidate) === selectedId
  );
  const conversationDisplayId = linkConversationDisplayId(conv);
  if (
    !conversationDisplayId ||
    !conversationBelongsToLinkContext(conv, context)
  ) {
    return;
  }
  const controller = new AbortController();
  linkConversationSaveController?.abort();
  linkConversationSaveController = controller;
  linkConversationSaving.value = true;
  try {
    await PipelineAPI.updateCard(
      context.cardId,
      { conversation_display_id: conversationDisplayId },
      {
        accountId: context.accountId,
        signal: controller.signal,
      }
    );
    if (controller.signal.aborted || !linkContextMatches(context)) return;
    showLinkConversationModal.value = false;
    emit('item-updated');
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.CONVERSATION_LINKED'),
      action: { type: 'success' },
    });
  } catch {
    if (controller.signal.aborted || !linkContextMatches(context)) return;
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.CONVERSATION_LINK_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    if (linkContextMatches(context)) linkConversationSaving.value = false;
    if (linkConversationSaveController === controller) {
      linkConversationSaveController = null;
    }
  }
};

const closeLinkConversationModal = () => {
  cancelLinkConversationRequests();
};

watch(
  () => Object.values(currentConversationContext()),
  (_context, previousContext) => {
    if (previousContext) cancelLinkConversationRequests();
  },
  { flush: 'sync' }
);

onBeforeUnmount(() => cancelLinkConversationRequests());

// Handle delete card
const currentDeleteContext = () => ({
  accountId: normalizePositiveId(store.getters.getCurrentAccountId),
  cardId: normalizePositiveId(props.item?.id),
  canDelete: Boolean(canDeleteCard.value),
});
const deleteContextMatches = context => {
  if (!context) return false;
  const currentContext = currentDeleteContext();
  return (
    currentContext.accountId === context.accountId &&
    currentContext.cardId === context.cardId &&
    currentContext.canDelete &&
    context.canDelete
  );
};
const closeDeleteConfirmModal = () => {
  showDeleteConfirmModal.value = false;
  deleteConfirmationContext.value = null;
};
const invalidateDeleteAction = () => {
  deleteController?.abort();
  deleteController = null;
  isDeleting.value = false;
  closeDeleteConfirmModal();
};

const handleDeleteCard = async () => {
  const context = deleteConfirmationContext.value
    ? { ...deleteConfirmationContext.value }
    : null;
  if (!deleteContextMatches(context) || isDeleting.value) return;
  const cardSnapshot = { ...props.item };
  const controller = new AbortController();
  deleteController = controller;
  try {
    isDeleting.value = true;
    await PipelineAPI.deleteCard(context.cardId, {
      accountId: context.accountId,
      currentCard: cardSnapshot,
      signal: controller.signal,
    });
    if (controller.signal.aborted || !deleteContextMatches(context)) return;
    closeDeleteConfirmModal();
    showQuickActionsMenu.value = false;
    emit('deleted');
  } catch (error) {
    if (controller.signal.aborted || !deleteContextMatches(context)) return;
    // eslint-disable-next-line no-console
    console.error('[PipelineCardDetails] handleDeleteCard failed:', error);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_DELETED_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    if (deleteController === controller) {
      isDeleting.value = false;
      deleteController = null;
    }
  }
};

const openDeleteConfirmModal = () => {
  const context = currentDeleteContext();
  if (!context.accountId || !context.cardId || !context.canDelete) return;
  showQuickActionsMenu.value = false;
  deleteConfirmationContext.value = context;
  showDeleteConfirmModal.value = true;
};

watch(
  () => Object.values(currentDeleteContext()),
  (_context, previousContext) => {
    if (previousContext) invalidateDeleteAction();
  },
  { flush: 'sync' }
);

onBeforeUnmount(invalidateDeleteAction);

// Computed properties for statistics card - REMOVED, MOVED TO PipelineItemStatistics.vue
// const itemStatistics = computed(() => {
//   const timeInStage = props.item.stage_entered_at
//     ? formatDistanceToNow(new Date(props.item.stage_entered_at), { locale: ptBR, addSuffix: false })
//     : t('PIPELINE.STATISTICS.NOT_AVAILABLE');
//
//   const noteCount = notes.value?.length || 0;
//   const checklistProgressText = `${completedItems.value}/${totalItems.value}`;
//   const attachmentCount = getAllAttachments.value?.length || 0;
//
//   return [
//     { icon: 'book-clock', label: t('PIPELINE.STATISTICS.TIME_IN_STAGE'), value: timeInStage },
//     { icon: 'comment-add', label: t('PIPELINE.STATISTICS.NOTES'), value: noteCount },
//     { icon: 'checkmark-circle', label: t('PIPELINE.STATISTICS.CHECKLIST'), value: checklistProgressText },
//     { icon: 'attach', label: t('PIPELINE.STATISTICS.ATTACHMENTS'), value: attachmentCount },
//   ];
// });
</script>

<template>
  <div class="flex flex-col h-full bg-n-background overflow-hidden">
    <!-- Header -->
    <PipelineCardHero
      :conversation-data="conversationData"
      :contact-avatar-url="contactAvatarUrl"
      :contact-name="contactName"
      :card-title="cardTitle"
      :show-subtitle="showSubtitle"
      :current-stage-name="currentStageName"
      :has-linked-conversation="hasLinkedConversation"
      :is-admin="isAdmin"
      :item="item"
      @close="$emit('close')"
      @navigate-to-conversation="navigateToConversation"
      @send-message="handleQuickAction('send_message')"
      @delete-card="openDeleteConfirmModal"
      @update-title="handleTitleUpdate"
      @update-priority="handlePriorityUpdate"
      @request-mark-won="handleRequestMarkWon"
      @request-mark-lost="handleRequestMarkLost"
      @request-reopen="handleRequestReopen"
      @open-related-items="showRelatedDrawer = true"
    />

    <!-- Stepper -->
    <div class="border-b border-n-weak bg-n-solid-2 px-6 py-3 flex-shrink-0">
      <PipelineStageStepper
        :stages="orderedStages"
        :current-stage-id="item.pipeline_stage"
        :is-won="!!item.won_at"
        :is-lost="!!item.lost_at"
        :disabled="isLoadingMove"
        @move-to-stage="handleMoveToStage"
        @request-terminal-move="handleRequestTerminalMove"
      />
    </div>

    <div
      v-if="conversationDataLoading"
      role="status"
      class="mx-4 mt-3 rounded-lg border border-n-weak bg-n-alpha-1 px-4 py-3 text-sm text-n-slate-11"
    >
      {{ t('PIPELINE.LOADING') }}
    </div>
    <div
      v-else-if="conversationDataError"
      role="alert"
      class="mx-4 mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-ruby-5 bg-n-ruby-2 px-4 py-3"
    >
      <p class="text-sm font-medium text-n-ruby-11">
        {{ t('PIPELINE.MESSAGES.CONVERSATION_LOAD_ERROR') }}
      </p>
      <button
        type="button"
        class="inline-flex min-h-11 items-center justify-center rounded-lg border border-n-ruby-6 bg-n-solid-1 px-4 py-2 text-sm font-medium text-n-ruby-11 transition-colors hover:bg-n-ruby-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-ruby-8"
        @click="retryConversationData"
      >
        {{ t('PIPELINE.MESSAGES.RETRY') }}
      </button>
    </div>

    <!-- Mobile drawer toggle buttons (hidden on lg+) -->
    <div
      class="lg:hidden flex items-center gap-2 px-4 py-2 border-b border-n-weak bg-n-solid-1 flex-shrink-0"
    >
      <button
        type="button"
        class="flex min-h-11 items-center gap-1.5 px-3 py-2 text-xs font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        @click="showLeftDrawer = true"
      >
        <fluent-icon icon="info" size="14" />
        <span>{{ t('PIPELINE.SHORTCUTS.OPEN_DETAILS') }}</span>
      </button>
      <button
        type="button"
        class="flex min-h-11 items-center gap-1.5 px-3 py-2 text-xs font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        @click="showRightDrawer = true"
      >
        <fluent-icon icon="person" size="14" />
        <span>{{ t('PIPELINE.SHORTCUTS.OPEN_ASSIGNEES') }}</span>
      </button>
    </div>

    <!-- 3-column body -->
    <div class="flex-1 grid grid-cols-12 gap-4 px-4 py-4 overflow-hidden">
      <!-- Sidebar esquerda: CardInfoPanel (desktop only) -->
      <!-- Auditoria 2026-08-08 (G3): os critérios de qualificação do funil
           existiam no backend — endpoint, store, 30 chaves de tradução — e não
           tinham tela. Ficam empilhados sob o painel de informações, dentro da
           MESMA coluna: o grid é de 12, e uma quarta coluna de 3 estouraria a
           linha (cross-review Codex). -->
      <div class="hidden lg:flex col-span-3 flex-col gap-4 overflow-y-auto">
        <CardInfoPanel
          :item="item"
          :custom-attributes="customAttributes"
          :contact-custom-attributes="contactCustomAttributes"
          :notes-count="notes.length"
          :checklist-total="checklistItems.length"
          :checklist-completed="checklistItems.filter(c => c.completed).length"
          :attachments-count="attachmentsCount"
          :stage-changes-count="stageChangesCount"
          @go-to-tab="activeTab = $event"
          @update-field="handlePanelFieldUpdate"
          @link-conversation="handleLinkConversation"
          @unlink-conversation="handleUnlinkConversation"
          @override-lead-score="showLeadScoreModal = true"
          @item-updated="emit('item-updated')"
        />

        <QualificationPanel
          v-if="qualificationCriteria.length > 0"
          :criteria="qualificationCriteria"
          :checklist="qualificationChecklist"
          :score="item.qualification_score || 0"
          :disabled="savingQualification"
          @update="handleQualificationUpdate"
        />

        <!-- Auditoria 2026-08-08 (G2): o componente de sincronização com o
             Google Agenda existia e nenhum lugar o importava — a feature era
             invisível mesmo estando pronta. -->
        <GoogleCalendarSync :item="item" @updated="emit('item-updated')" />
      </div>

      <!-- No mobile a sidebar não existe, e sem isto a qualificação e a
           sincronização com o Agenda ficariam inacessíveis abaixo de lg
           (cross-review Codex). -->
      <QualificationPanel
        v-if="qualificationCriteria.length > 0"
        class="col-span-12 lg:hidden max-h-72 overflow-y-auto"
        :criteria="qualificationCriteria"
        :checklist="qualificationChecklist"
        :score="item.qualification_score || 0"
        :disabled="savingQualification"
        @update="handleQualificationUpdate"
      />

      <GoogleCalendarSync
        class="col-span-12 lg:hidden"
        :item="item"
        @updated="emit('item-updated')"
      />

      <!-- Centro: tabs + conteúdo -->
      <div
        class="col-span-12 lg:col-span-6 flex flex-col bg-n-solid-2 rounded-xl border border-n-weak overflow-hidden"
      >
        <!-- Tabs nav horizontal -->
        <div
          class="flex border-b border-n-weak bg-n-solid-1 overflow-x-auto flex-shrink-0"
          role="tablist"
          :aria-label="t('PIPELINE.DETAILS')"
        >
          <button
            v-for="(tab, index) in tabs"
            :id="`pipeline-card-tab-${tab.id}`"
            :key="tab.id"
            type="button"
            role="tab"
            :aria-selected="activeTab === tab.id"
            :aria-controls="`pipeline-card-panel-${tab.id}`"
            :tabindex="activeTab === tab.id ? 0 : -1"
            class="flex min-h-11 items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand"
            :class="
              activeTab === tab.id
                ? 'border-n-brand text-n-brand'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="activeTab = tab.id"
            @keydown="handleTabKeydown($event, index)"
          >
            <fluent-icon :icon="tab.icon" size="16" />
            <span>{{ tab.label }}</span>
          </button>
        </div>

        <!-- Tab content -->
        <div
          :id="`pipeline-card-panel-${activeTab}`"
          role="tabpanel"
          :aria-labelledby="`pipeline-card-tab-${activeTab}`"
          class="flex-1 overflow-y-auto p-4 sm:p-6"
          tabindex="0"
        >
          <!-- Tab: Notes -->
          <NotesTab
            v-if="activeTab === 'notes'"
            :item="item"
            :notes="notes"
            @item-updated="emit('item-updated')"
            @update:notes="notes = $event"
          />

          <!-- Tab: Checklist -->
          <ChecklistTab
            v-else-if="activeTab === 'checklist'"
            :item="item"
            :checklist-items="checklistItems"
            @item-updated="emit('item-updated')"
            @update:checklist-items="checklistItems = $event"
            @go-to-tab="activeTab = $event"
          />

          <!-- Tab: Attachments -->
          <AttachmentsTab
            v-else-if="activeTab === 'attachments'"
            :item="item"
            :notes="notes"
            @item-updated="emit('item-updated')"
            @go-to-tab="activeTab = $event"
          />

          <!-- Tab: Scheduled Messages (replaces legacy Sequences tab) -->
          <ScheduledMessagesTab
            v-else-if="activeTab === 'scheduled'"
            :item="item"
            @item-updated="emit('item-updated')"
          />

          <!-- Tab: Atividades do CRM (Fase 61 — G1) -->
          <CrmActivitiesTab
            v-else-if="activeTab === 'crm-activities'"
            :item="item"
            @item-updated="emit('item-updated')"
          />

          <!-- Tab: Oportunidades (Fase 59) — cross feature opt-in -->
          <OpportunitiesTab
            v-else-if="activeTab === 'opportunities' && isOpportunitiesEnabled"
            :item="item"
          />
        </div>
      </div>

      <!-- Sidebar direita: CardAssigneePanel (desktop only) -->
      <CardAssigneePanel
        class="hidden lg:block col-span-3 overflow-y-auto"
        :item="item"
        :agents="accountAgents"
        :assigned-agent-ids="assignedAgentIds"
        @assign-agents="handleAssignAgents"
        @item-updated="emit('item-updated')"
      />
    </div>

    <!-- Mobile left drawer: CardInfoPanel -->
    <Modal
      v-model:show="showLeftDrawer"
      modal-type="left-aligned"
      size="!w-80 !max-w-[85vw]"
      :show-close-button="false"
      @close="showLeftDrawer = false"
    >
      <div
        class="flex min-h-full flex-col"
        data-testid="pipeline-card-details-drawer"
      >
        <div
          class="flex items-center justify-between p-4 border-b border-n-weak"
        >
          <h2
            id="pipeline-card-details-drawer-title"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ t('PIPELINE.SHORTCUTS.OPEN_DETAILS') }}
          </h2>
          <button
            type="button"
            class="min-h-11 min-w-11 rounded-lg p-2 hover:bg-n-alpha-1 text-n-slate-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :aria-label="t('PIPELINE.CLOSE_MODAL')"
            @click="showLeftDrawer = false"
          >
            <fluent-icon icon="dismiss" size="16" />
          </button>
        </div>
        <div class="p-4">
          <CardInfoPanel
            :item="item"
            :custom-attributes="customAttributes"
            :contact-custom-attributes="contactCustomAttributes"
            :notes-count="notes.length"
            :checklist-total="checklistItems.length"
            :checklist-completed="
              checklistItems.filter(c => c.completed).length
            "
            :attachments-count="attachmentsCount"
            :stage-changes-count="stageChangesCount"
            @go-to-tab="
              activeTab = $event;
              showLeftDrawer = false;
            "
            @update-field="handlePanelFieldUpdate"
            @link-conversation="handleLinkConversation"
            @unlink-conversation="handleUnlinkConversation"
            @override-lead-score="showLeadScoreModal = true"
            @item-updated="emit('item-updated')"
          />
        </div>
      </div>
    </Modal>

    <!-- Mobile right drawer: CardAssigneePanel -->
    <Modal
      v-model:show="showRightDrawer"
      modal-type="right-aligned"
      size="!w-80 !max-w-[85vw]"
      :show-close-button="false"
      @close="showRightDrawer = false"
    >
      <div
        class="flex min-h-full flex-col"
        data-testid="pipeline-card-assignees-drawer"
      >
        <div
          class="flex items-center justify-between p-4 border-b border-n-weak"
        >
          <h2
            id="pipeline-card-assignees-drawer-title"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ t('PIPELINE.SHORTCUTS.OPEN_ASSIGNEES') }}
          </h2>
          <button
            type="button"
            class="min-h-11 min-w-11 rounded-lg p-2 hover:bg-n-alpha-1 text-n-slate-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :aria-label="t('PIPELINE.CLOSE_MODAL')"
            @click="showRightDrawer = false"
          >
            <fluent-icon icon="dismiss" size="16" />
          </button>
        </div>
        <div class="p-4">
          <CardAssigneePanel
            :item="item"
            :agents="accountAgents"
            :assigned-agent-ids="assignedAgentIds"
            @assign-agents="handleAssignAgents"
            @item-updated="emit('item-updated')"
          />
        </div>
      </div>
    </Modal>

    <!-- Modals -->

    <!-- Link Conversation Modal -->
    <Modal
      v-model:show="showLinkConversationModal"
      :on-close="closeLinkConversationModal"
    >
      <div class="flex flex-col gap-4 p-6 min-w-[320px] max-w-md">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('PIPELINE.PANELS.LINK_CONVERSATION_BTN') }}
        </h3>
        <div
          v-if="linkConversationLoading"
          role="status"
          class="text-sm text-n-slate-11"
        >
          {{ t('PIPELINE.LOADING') }}
        </div>
        <div
          v-else-if="linkConversationError"
          role="alert"
          class="flex flex-col items-start gap-3 rounded-lg border border-n-ruby-5 bg-n-ruby-2 p-4"
        >
          <p class="text-sm font-medium text-n-ruby-11">
            {{ t('PIPELINE.MESSAGES.CONVERSATIONS_LOAD_ERROR') }}
          </p>
          <button
            type="button"
            class="inline-flex min-h-11 items-center justify-center rounded-lg border border-n-ruby-6 bg-n-solid-1 px-4 py-2 text-sm font-medium text-n-ruby-11 transition-colors hover:bg-n-ruby-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-ruby-8"
            @click="retryLinkConversations"
          >
            {{ t('PIPELINE.MESSAGES.RETRY') }}
          </button>
        </div>
        <div
          v-else-if="linkConversationList.length === 0"
          class="text-sm text-n-slate-11"
        >
          {{ t('PIPELINE.MESSAGES.NO_CONVERSATIONS_FOR_CONTACT') }}
        </div>
        <div
          v-else
          role="radiogroup"
          :aria-label="t('PIPELINE.PANELS.LINKED_CONVERSATION')"
          class="flex max-h-64 flex-col gap-2 overflow-y-auto overscroll-contain"
        >
          <label
            v-for="conv in linkConversationList"
            :key="linkConversationDisplayId(conv)"
            class="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors focus-within:ring-2 focus-within:ring-n-brand"
            :class="
              selectedConversationId === conv.id
                ? 'border-n-brand bg-n-alpha-1'
                : 'border-n-weak hover:bg-n-alpha-1'
            "
          >
            <input
              v-model="selectedConversationId"
              type="radio"
              :value="linkConversationDisplayId(conv)"
              class="mt-0.5"
            />
            <div class="flex flex-col gap-0.5 min-w-0">
              <span class="text-sm font-medium text-n-slate-12">
                {{ `#${linkConversationDisplayId(conv)}` }}
              </span>
              <span class="text-xs text-n-slate-11 truncate">
                {{ conv.meta?.channel || conv.inbox_id }}
              </span>
            </div>
          </label>
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <button
            type="button"
            class="min-h-11 rounded-lg border border-n-weak px-4 py-2 text-sm text-n-slate-11 transition-colors hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            @click="closeLinkConversationModal"
          >
            {{ t('CANCEL') }}
          </button>
          <button
            type="button"
            class="min-h-11 rounded-lg bg-n-brand px-4 py-2 text-sm text-white transition-colors hover:bg-n-brand-dark disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :disabled="!selectedConversationId || linkConversationSaving"
            :aria-busy="linkConversationSaving"
            @click="confirmLinkConversation"
          >
            {{
              linkConversationSaving
                ? t('PIPELINE.SAVING')
                : t('PIPELINE.PANELS.LINK_CONVERSATION_BTN')
            }}
          </button>
        </div>
      </div>
    </Modal>

    <Modal
      v-model:show="showMessageModal"
      :on-close="() => (showMessageModal = false)"
    >
      <SendMessageTemplate
        :item="item"
        :pipeline-id="item.pipeline_id"
        :conversation-id="conversationData?.id || item.conversation_display_id"
        :current-stage="item.pipeline_stage"
        :contact="conversationData?.contact || {}"
        :conversation="conversationData || {}"
        @close="showMessageModal = false"
        @sent="handleMessageSent"
      />
    </Modal>

    <!-- Related Items drawer (slides in from right, triggered from header) -->
    <Modal
      v-model:show="showRelatedDrawer"
      modal-type="right-aligned"
      size="!w-full !max-w-2xl"
      :show-close-button="false"
      @close="showRelatedDrawer = false"
    >
      <div
        class="flex min-h-full flex-col"
        data-testid="pipeline-card-related-drawer"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0"
        >
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('PIPELINE.RELATED.TITLE') }}
          </h2>
          <button
            type="button"
            class="min-h-11 min-w-11 p-2 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :aria-label="t('PIPELINE.CLOSE_MODAL')"
            @click="showRelatedDrawer = false"
          >
            <fluent-icon icon="dismiss" size="18" />
          </button>
        </div>
        <div class="flex-1 overflow-y-auto p-6">
          <RelatedItemsTab
            :item="item"
            @navigate-to-card="showRelatedDrawer = false"
            @navigating="showRelatedDrawer = false"
          />
        </div>
      </div>
    </Modal>

    <!-- Mark as Won Modal -->
    <MarkAsWonModal
      :item="item"
      :show="showWonModal"
      @close="showWonModal = false"
      @success="handleWonSuccess"
    />

    <!-- Mark as Lost Modal -->
    <MarkAsLostModal
      :item="item"
      :show="showLostModal"
      @close="showLostModal = false"
      @success="handleLostSuccess"
    />

    <!-- Lead Score Override Modal -->
    <LeadScoreOverrideModal
      :pipeline-card="item"
      :show="showLeadScoreModal"
      @close="showLeadScoreModal = false"
      @updated="emit('item-updated')"
    />

    <!-- Delete Confirmation Modal -->
    <Modal
      v-model:show="showDeleteConfirmModal"
      :on-close="closeDeleteConfirmModal"
    >
      <div class="p-6 max-w-md mx-auto">
        <div class="flex items-center gap-3 mb-4">
          <div
            class="w-10 h-10 rounded-full bg-n-ruby-3 flex items-center justify-center"
          >
            <fluent-icon icon="delete" size="20" class="text-n-ruby-11" />
          </div>
          <h3 class="text-heading-2 text-n-slate-12">
            {{ t('PIPELINE.CARD.DELETE_CONFIRM_TITLE') }}
          </h3>
        </div>
        <p class="text-sm text-n-slate-11 mb-6">
          {{ t('PIPELINE.CARD.DELETE_CONFIRM_MESSAGE') }}
        </p>
        <div class="flex justify-end gap-3">
          <button
            type="button"
            class="min-h-11 rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :disabled="isDeleting"
            @click="closeDeleteConfirmModal"
          >
            {{ t('PIPELINE.CARD.CANCEL') }}
          </button>
          <button
            type="button"
            class="min-h-11 rounded-lg bg-n-ruby-9 px-4 py-2 text-sm font-medium text-white transition-colors hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:opacity-50"
            :disabled="isDeleting"
            @click="handleDeleteCard"
          >
            <span v-if="isDeleting" class="flex items-center gap-2">
              <span
                class="h-4 w-4 rounded-full border-2 border-white border-t-transparent motion-safe:animate-spin"
              />
              {{ t('PIPELINE.CARD.DELETING') }}
            </span>
            <span v-else>{{ t('PIPELINE.CARD.DELETE') }}</span>
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>
